import PropTypes from 'prop-types';
import React, { useState, useMemo, useLayoutEffect, useEffect } from 'react';
import { connect } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';

import { addPageData, navigateWizard } from '../../actions';
import AddActionBar from '../../components/AddActionBar/AddActionBar';
import Button from '../../components/Button';
import CitizenshipItemBox from '../../components/CitizenshipItemBox';
import PageLayout from '../../components/PageLayout';
import SelectBase from '../../components/SelectBase';
import analytics from '../../lib/analytics';
import useCountries from '../../lib/hooks/useCountries';
import * as builderFactory from '../../lib/pageDataPayloadFactory';
import { selectedCitizenshipCountryCodesSelector } from '../../reducers/selectors';

const CitizenshipPage = ({
  selectedCitizenshipCountryCodes,
  dispatchAddPageData,
  dispatchWizardNav,
}) => {
  const [error, setError] = useState(null);
  const [defaultInitialized, setDefaultInitialized] = useState(false);
  const [selectedCitizenshipCountries, setSelectedCitizenshipCountries] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const { data: countries } = useCountries();
  const [editState, setEditState] = useState(false);

  const sortCountriesAlphabetical = countriesArray => {
    return countriesArray.slice().sort((a, b) => {
      if (a.label < b.label) {
        return -1; // A comes before B
      } else if (a.label > b.label) {
        return 1; // A comes after B
      } else {
        return 0; // Values are equal
      }
    });
  };

  useLayoutEffect(() => {
    const haveCountries = countries.length !== 0;
    if (haveCountries && !defaultInitialized) {
      setSelectedCitizenshipCountries(
        countries.filter(country => {
          return selectedCitizenshipCountryCodes.indexOf(country.value) !== -1;
        }),
      );
      setDefaultInitialized(true);
    }
  }, [selectedCitizenshipCountryCodes, countries, defaultInitialized]);

  useLayoutEffect(() => {
    const haveCountries = countries.length !== 0;
    if (haveCountries) {
      const sortedCountries = sortCountriesAlphabetical(countries);
      const countryCodes = selectedCitizenshipCountries.map(country => country.value);
      setCountryOptions(sortedCountries.filter(country => !countryCodes.includes(country.value)));
    }
  }, [selectedCitizenshipCountries, countries]);

  useEffect(() => {
    if (!selectedCitizenshipCountries.length) {
      setEditState(true);
    } else {
      setEditState(false);
    }
  }, [selectedCitizenshipCountries]);

  const isInvalid = useMemo(() => {
    if (selectedCitizenshipCountries.length === 0) {
      return true;
    }

    if (selectedCitizenshipCountries.length > 2) {
      setError('You only need to tell us about your primary and secondary citizenships');
      return true;
    }

    setError(null);
    return false;
  }, [selectedCitizenshipCountries]);

  const handleSubmit = event => {
    event.preventDefault();
    if (isInvalid) return;

    analytics.sendEvent('Click - Next', 'citizenship');

    const citizenshipCountryCodes = selectedCitizenshipCountries.map(
      citizenship => citizenship.value,
    );

    dispatchAddPageData(
      builderFactory.createAddPageDataPayload(
        'citizenshipCountries',
        selectedCitizenshipCountries,
        'citizenshipCountries',
        citizenshipCountryCodes,
      ),
    );

    dispatchWizardNav({ dir: 'fwd' });
  };

  const setCountries = (selectedCountries, removedOption) => {
    setSelectedCitizenshipCountries([...selectedCountries]);
    setCountryOptions(countryOptions.filter(country => !selectedCountries.includes(country)));
    if (removedOption) {
      const sortedCountries = sortCountriesAlphabetical([...countryOptions, removedOption]);
      setCountryOptions(sortedCountries);
    }

    setEditState(false);
  };

  const updateCitizenship = (citizen, index) => {
    let citizenships = [...selectedCitizenshipCountries];
    citizenships[index] = citizen;
    setSelectedCitizenshipCountries([...citizenships]);
  };

  const removeCitizenship = index => {
    let citizenships = [...selectedCitizenshipCountries];
    citizenships.splice(index, 1);
    setSelectedCitizenshipCountries([...citizenships]);
  };

  const renderError = () => {
    if (isInvalid && error !== null) {
      return <div className="row-spacer text-error">{error}</div>;
    }
    return null;
  };

  const renderResidenceHistories = selectedCitizenshipCountries.map((citizenship, index) => {
    const { label, value } = citizenship;
    return (
      <CSSTransition key={`${label}-${value}`} timeout={300} classNames="fade-list-item">
        <CitizenshipItemBox
          selectedOption={citizenship}
          options={countryOptions}
          buttonId={index}
          updateCitizenship={updateCitizenship}
          removeCitizenship={removeCitizenship}
        />
      </CSSTransition>
    );
  });

  return (
    <PageLayout
      title="What citizenships do you have?"
      helpText="Your citizenship is only used to measure social impact and manage investor requirements, and won’t affect your chances to get a loan."
    >
      <p className="mt-4">If you have a second citizenship, please list it as well.</p>
      <form onSubmit={handleSubmit}>
        <div>
          <ol className="list-hidden mt-4">
            <TransitionGroup component={null}>{renderResidenceHistories}</TransitionGroup>
          </ol>
        </div>
        <div className="mt-4">
          {editState && (
            <SelectBase
              selectedValue={selectedCitizenshipCountries}
              onChange={setCountries}
              options={countryOptions}
              placeholder="Search for your country"
              noOptionsMessage="We couldn't find that country."
              label="Citizenship"
              type="multi"
            />
          )}
        </div>
        {renderError()}
        <div className={editState ? 'mt-8' : 'mt-4'}>
          <AddActionBar
            setState={setEditState}
            disable={!editState}
            child={'+ Add another citizenship'}
          />
        </div>

        <div className="mt-12">
          <Button type="submit" className="niso-button" isDisabled={isInvalid}>
            Next
          </Button>
        </div>
      </form>
    </PageLayout>
  );
};

CitizenshipPage.propTypes = {
  selectedCitizenshipCountryCodes: PropTypes.arrayOf(PropTypes.string),
  dispatchAddPageData: PropTypes.func.isRequired,
  dispatchWizardNav: PropTypes.func.isRequired,
};

CitizenshipPage.defaultProps = {
  selectedCitizenshipCountryCodes: [],
};

export const mapStateToProps = state => ({
  selectedCitizenshipCountryCodes: selectedCitizenshipCountryCodesSelector(state),
});

export const mapDispatchToProps = {
  dispatchAddPageData: addPageData,
  dispatchWizardNav: navigateWizard,
};

export { CitizenshipPage };

export default connect(mapStateToProps, mapDispatchToProps)(CitizenshipPage);
